// URL-linked pagination for the crawlable feeds.
//
// Googlebot renders but does not scroll, so an infinite-scroll feed exposes
// exactly one batch to it. These helpers give each feed a real paginated URL
// space (?page=N) and a real next-page <a href> in the rendered DOM:
// crawlers walk the chain page by page, humans keep infinite scroll (the
// anchor's click handler just loads the next batch inline).
import { memeHubPath, streamHubPath } from 'lib/seoPaths'

export const MAX_FEED_PAGE = 500

// Keep deep linked pages out of the normal page-1 cache while preserving the
// base key as a prefix for broad invalidation.
export function linkedFeedQueryKey(
    queryKey: readonly unknown[],
    initialPage: number,
): readonly unknown[] {
    return initialPage > 1 ? [...queryKey, `seoPage:${initialPage}`] : queryKey
}

// The URL-addressable path for a feed filter, when it has one. Only feeds
// with their own public URL take part in linked pagination; panels opened
// over other routes (openRight etc.) return undefined and paginate silently.
export function feedPathFromFilter(
    filter?: {
        meme?: string
        memes?: boolean
        memes_scope?: string
        streams?: boolean
        streams_scope?: string
        team_ids?: string[]
    } | null,
): string | undefined {
    if (!filter) return undefined
    if (filter.meme) return memeHubPath(filter.meme)
    if (filter.memes) return filter.memes_scope ? undefined : '/memes'
    if (filter.streams) return filter.streams_scope ? undefined : '/streams'
    if (Array.isArray(filter.team_ids) && filter.team_ids.length === 1) {
        return streamHubPath(filter.team_ids[0])
    }
    return undefined
}

// The page a hard load of feedPath should start at (?page=N). Only consults
// the URL when the browser is actually AT that path — client-side navigation
// never carries ?page, so in-app panels always start at 1.
export function initialFeedPageFromUrl(feedPath?: string): number {
    if (!feedPath || typeof window === 'undefined') return 1
    if (window.location.pathname !== feedPath) return 1
    const raw = new URLSearchParams(window.location.search).get('page')
    const page = Number(raw)
    if (!Number.isInteger(page) || page < 2) return 1
    return Math.min(page, MAX_FEED_PAGE)
}

export function feedPageHref(feedPath: string, page: number): string {
    return page > 1 ? `${feedPath}?page=${page}` : feedPath
}

export function shouldLoadFeedPageInPlace(event: {
    button: number
    ctrlKey: boolean
    metaKey: boolean
    shiftKey: boolean
    altKey: boolean
    defaultPrevented: boolean
}): boolean {
    return (
        !event.defaultPrevented &&
        event.button === 0 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey &&
        !event.altKey
    )
}
